import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useActiveStore } from "../lib/activeStore.js";
import { canManage } from "../lib/roles.js";
import { createUnit, authorFromProfile } from "../lib/data.js";

// Add a new unit to inventory. Admin-only. Used while we don't yet have an
// inventory parser / OEM feed wired — managers can type units in directly.
// Once the parser lands, this page stays useful for one-off additions.
export function AddUnit() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  // Write to the store the user is actively viewing (topbar switcher), not the
  // default profile.storeId — otherwise a multi-store admin who switched stores
  // would silently create units in the wrong store.
  const activeStore = useActiveStore() || profile?.storeId || null;

  const [stockNumber, setStockNumber] = useState("");
  const [year, setYear]               = useState("");
  const [make, setMake]               = useState("BMW");
  const [model, setModel]             = useState("");
  const [trim, setTrim]               = useState("");
  const [vin, setVin]                 = useState("");
  const [department, setDepartment]   = useState("new-car");
  const [voltage, setVoltage]         = useState("");
  const [voltageType, setVoltageType] = useState("12V");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  if (!canManage(profile)) {
    return (
      <main className="container">
        <div className="panel" style={{ maxWidth: 480, margin: "60px auto" }}>
          <div className="eyebrow">Restricted</div>
          <h2 style={{ marginTop: 10 }}>Adding units is admin-only.</h2>
          <p style={{ marginTop: 10 }}>
            Ask a store admin to grant you the manager role on Battery Care if you need to add inventory.
          </p>
          <Link to="/today" className="btn" style={{ marginTop: 14 }}>← Today</Link>
        </div>
      </main>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);

    // Validation
    if (!stockNumber.trim()) return setError(new Error("Stock # is required"));
    const yearN = parseInt(year, 10);
    if (!yearN || yearN < 1990 || yearN > 2100) return setError(new Error("Year must be 1990-2100"));
    if (!make.trim() || !model.trim()) return setError(new Error("Make and model are required"));
    if (!profile?.tenantId || !activeStore) {
      return setError(new Error("Your account isn't linked to a store yet. Ask an admin to set your tenant + store, then try again."));
    }
    let voltageN = null;
    if (voltage.trim()) {
      voltageN = parseFloat(voltage);
      if (Number.isNaN(voltageN)) return setError(new Error("Voltage must be a number (e.g. 12.6)"));
    }

    setSubmitting(true);
    try {
      const id = await createUnit({
        tenantId:    profile.tenantId,
        storeId:     activeStore,
        department,
        stockNumber: stockNumber.trim(),
        vin:         vin.trim(),
        year:        yearN,
        make:        make.trim(),
        model:       model.trim(),
        trim:        trim.trim(),
        voltage:     voltageN,
        voltageType,
        by:          authorFromProfile(profile),
      });
      // Land the user on the new unit's check-in page so they can record a
      // first reading. If they didn't enter voltage above, they can do it
      // here. If they did, they'll see the doc with their initial reading.
      navigate(`/check/${id}`);
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  }

  return (
    <main className="container tight">
      <Link to="/manager" className="ci-back">← Manager</Link>

      <div className="eyebrow" style={{ marginTop: 18 }}>Add a unit</div>
      <h1 style={{ fontSize: 22, marginTop: 6 }}>New unit · {profile?.storeId || "—"}</h1>
      <p style={{ marginTop: 6, fontSize: 13, color: "var(--text-muted)" }}>
        Add an existing vehicle to inventory. Voltage is optional — you can record the first
        reading on the next screen.
      </p>

      <form onSubmit={onSubmit} style={{ marginTop: 18 }}>
        <div className="field">
          <label>Stock #</label>
          <input
            className="big"
            type="text"
            placeholder="B41-2891"
            value={stockNumber}
            onChange={(e) => setStockNumber(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>Year</label>
            <input type="text" inputMode="numeric" placeholder="2024" value={year}
                   onChange={(e) => setYear(e.target.value)} disabled={submitting} />
          </div>
          <div className="field">
            <label>Make</label>
            <input type="text" placeholder="BMW" value={make}
                   onChange={(e) => setMake(e.target.value)} disabled={submitting} />
          </div>
          <div className="field">
            <label>Model</label>
            <input type="text" placeholder="540i xDrive" value={model}
                   onChange={(e) => setModel(e.target.value)} disabled={submitting} />
          </div>
          <div className="field">
            <label>Trim</label>
            <input type="text" placeholder="(opt)" value={trim}
                   onChange={(e) => setTrim(e.target.value)} disabled={submitting} />
          </div>
        </div>

        <div className="field">
          <label>VIN <span style={{ textTransform: "none", opacity: 0.6 }}>· optional</span></label>
          <input type="text" placeholder="WBA53EH00RWY81234" value={vin}
                 onChange={(e) => setVin(e.target.value)} disabled={submitting} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>Department</label>
            <select value={department} onChange={(e) => setDepartment(e.target.value)} disabled={submitting}>
              <option value="new-car">New car</option>
              <option value="lot-tech">Lot tech</option>
            </select>
          </div>
          <div className="field">
            <label>Battery type</label>
            <select value={voltageType} onChange={(e) => setVoltageType(e.target.value)} disabled={submitting}>
              <option value="12V">12V starter</option>
              <option value="HV">HV traction</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Initial voltage <span style={{ textTransform: "none", opacity: 0.6 }}>· optional, can record on next screen</span></label>
          <input type="text" inputMode="decimal" placeholder="12.62" value={voltage}
                 onChange={(e) => setVoltage(e.target.value)} disabled={submitting} />
        </div>

        {error && (
          <div style={{ color: "var(--danger)", marginBottom: 14, fontSize: 13 }}>
            {error.message || String(error)}
          </div>
        )}

        <button type="submit" className="ci-submit" disabled={submitting}>
          {submitting ? "Adding…" : "Add unit"}
        </button>
        <Link to="/manager" className="ci-cancel">Cancel</Link>
      </form>
    </main>
  );
}
